import {
	ApiClient,
	AsyncResult,
	FileUploadUrlApiRequestBody,
	FileUploadUrlApiResponseBody,
	RedirectionStatus,
	SuccessStatus,
	err,
	isErr,
	ok,
} from "@jace/api";
import { noop } from "@zetalabs/core";
import { FileUpload, ProcessingStatus, TerminationStatus } from "../types";

type Path = string[];
type EventHandler = (data: FileUpload) => void;

export async function getFileUploadUrl(
	item: FileUpload,
	transport: ApiClient,
): AsyncResult<string> {
	const objectStorageDataResult =
		await transport.post<FileUploadUrlApiResponseBody>("/files/upload-url", {
			data: {
				file_name: item.name,
				mime_type: item.source?.type ?? "",
				thread_id: item.sessionId,
			} satisfies FileUploadUrlApiRequestBody,
		});

	if (isErr(objectStorageDataResult)) {
		return err(objectStorageDataResult.error);
	}

	const { unified_uri, url: uploadUrl } = objectStorageDataResult.value;
	item.unifiedUri = unified_uri;
	item.status = ProcessingStatus.InProgress;

	return ok(uploadUrl);
}

export async function executeUpload(
	targetUri: string,
	item: FileUpload,
	controller?: AbortController,
	onStart: EventHandler = noop,
	onChange: EventHandler = noop,
	onSuccess: EventHandler = noop,
	onError: EventHandler = noop,
): Promise<void> {
	{
		if (!item.source) {
			return;
		}

		const xhr = new XMLHttpRequest();
		const signal = controller?.signal;

		signal?.addEventListener("abort", () => {
			item.status = TerminationStatus.Cancelled;
			xhr.abort();
		});

		xhr.open("PUT", targetUri);
		xhr.setRequestHeader("Content-Type", item.source.type);

		xhr.upload.onprogress = (event) => {
			item.progress = event.loaded / event.total;
			onChange(item);
		};

		xhr.upload.onloadstart = (_event) => {
			item.progress = 0;
			onStart(item);
		};

		xhr.upload.onload = (_event) => {
			item.progress = 1;
			onChange(item);
		};

		xhr.onload = () => {
			if (
				xhr.status >= SuccessStatus.Ok &&
				xhr.status < RedirectionStatus.MultipleChoices
			) {
				item.status = TerminationStatus.Done;

				onSuccess(item);
			}
		};

		xhr.upload.onerror = () => {
			item.error = "During upload";
			onError(item);
		};

		const formData = new FormData();
		formData.append("file", item.source);
		xhr.send(formData);
	}
}
